import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowUpRight,
  Star,
  Cpu,
  Mail,
  Layers,
  Users,
  Activity,
  Phone,
  Award,
  Sparkles,
  Rocket,
  Globe2,
  ShieldCheck,
  Clock,
  MessageSquare,
  Compass,
  Code2,
  ArrowRight,
  Database,
  CloudCog,
  Quote,
} from "lucide-react"
import { useSettings } from "@/hooks/useSettings"
import { useServices } from "@/hooks/useServices"
import { useTechnologies } from "@/hooks/useTechnologies"
import { useTestimonials } from "@/hooks/useTestimonials"
import { AppIcon } from "@/lib/icon"
import { SectionSkeleton, ErrorState } from "@/components/state"
import { FloatingIcon } from "@/components/floating-icon"
import { pageMeta } from "@/lib/seo"

export const Route = createFileRoute("/_public/")({
  component: Home,
  head: () =>
    pageMeta({
      title: "Nexovia IT — Start Your Business World Wide",
      description:
        "Software engineering, cloud infrastructure and AI systems built to launch and scale your business globally.",
      path: "/",
    }),
})

const stats = [
  { k: "120+", v: "Products shipped", icon: Rocket },
  { k: "38", v: "Countries served", icon: Globe2 },
  { k: "99.98%", v: "Platform uptime", icon: ShieldCheck },
  { k: "24/7", v: "Engineering support", icon: Clock },
]

const journey = [
  { icon: MessageSquare, title: "Share Your Vision", body: "Tell us your goals, timeline and constraints in a free discovery call." },
  { icon: Compass, title: "We Design the Plan", body: "Our architects map the roadmap, stack and milestones before a line of code is written." },
  { icon: Code2, title: "We Build & Test", body: "Weekly ship cycles, continuous QA and full visibility into progress as we go." },
  { icon: Rocket, title: "You Launch & Scale", body: "We deploy, monitor and stay on for ongoing support as your business grows." },
]

function Home() {
  const { data: settings } = useSettings()
  const { data: services, isLoading: servicesLoading, isError: servicesError } = useServices()
  const { data: technologies, isLoading: technologiesLoading, isError: technologiesError } = useTechnologies()
  const { data: testimonials, isLoading: testimonialsLoading, isError: testimonialsError } = useTestimonials()

  const activeServices = (services ?? []).filter((s) => s.is_active).slice(0, 6)
  const activeTechnologies = (technologies ?? []).filter((t) => t.is_active)
  const activeTestimonials = (testimonials ?? []).filter((t) => t.is_active).slice(0, 3)

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-background">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(ellipse at 50% 40%, color-mix(in oklab, var(--rust-glow) 22%, transparent), transparent 55%), radial-gradient(ellipse at 90% 10%, color-mix(in oklab, var(--rust) 14%, transparent), transparent 55%)",
          }}
        />
        <div
          className="absolute inset-0 -z-10 opacity-40"
          style={{
            backgroundImage:
              "linear-gradient(color-mix(in oklab, var(--ink) 6%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in oklab, var(--ink) 6%, transparent) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        {/* Floating icon cards */}
        <FloatingIcon icon={Cpu} className="flex left-[4%] top-[4%] md:left-[8%] md:top-[14%]" delay={0} />
        <FloatingIcon icon={Mail} className="flex left-[3%] top-[30%] md:left-[3%] md:top-[38%]" delay={0.8} />
        <FloatingIcon icon={Layers} className="flex left-[5%] top-[60%] md:left-[10%] md:top-[62%]" delay={1.6} />
        <FloatingIcon icon={Users} className="flex left-[8%] bottom-[4%] md:hidden lg:flex lg:left-[16%] lg:bottom-[10%]" delay={2.4} />
        <FloatingIcon icon={Sparkles} className="flex right-[4%] top-[6%] md:right-[10%] md:top-[10%]" delay={0.4} />
        <FloatingIcon icon={Activity} className="flex right-[3%] top-[34%] md:right-[4%] md:top-[42%]" delay={1.2} />
        <FloatingIcon icon={Phone} className="flex right-[5%] top-[64%] md:right-[14%] md:top-[64%]" delay={2} />
        <FloatingIcon icon={Award} className="flex right-[8%] bottom-[4%] md:hidden lg:flex lg:right-[8%] lg:bottom-[10%]" delay={2.8} />

        <div className="relative mx-auto max-w-4xl px-6 py-28 lg:py-36 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-rust backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-rust" />
            Enterprise Software · Serving Worldwide
          </span>
          {/* Always the real heading, never a skeleton block: this was
              measured as the largest single layout-shift contributor on
              the page (Lighthouse CLS insight) — swapping a fixed-height
              skeleton for the actual h1 shifts everything below it the
              instant settings finishes loading. Rendering the fallback
              copy in the *same* h1 from the first paint means settings
              resolving just updates this element's text in place — same
              element, same font-size classes, no swap, so at worst the
              line count changes by one instead of the whole block's
              height changing. */}
          <h1 className="mt-8 text-5xl font-extrabold leading-[1.08] tracking-tight md:text-6xl lg:text-7xl text-balance">
            <span className="block text-rust">
              {settings?.hero_heading ?? "We Build Software"}
            </span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            {settings?.hero_subheading ??
              "Nexovia IT is a global software company delivering world-class solutions to clients across the world."}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to={settings?.hero_cta_link ?? "/contact"}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant hover:opacity-95"
            >
              {settings?.hero_cta_text ?? "Start a Project"} <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm font-semibold hover:bg-secondary"
            >
              Our Services
            </Link>
          </div>

          {/* Pill row */}
          <div className="mt-12 flex flex-wrap justify-center gap-2">
            {["AI Employees", "Mobile Apps", "Web Apps", "SaaS Platforms", "AI & ML"].map((p) => (
              <span key={p} className="rounded-full border border-border bg-background/80 px-4 py-2 text-sm font-medium backdrop-blur">
                {p}
              </span>
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <div className="inline-flex max-w-full items-center gap-3 rounded-full border border-border bg-background/80 px-4 py-2 shadow-card backdrop-blur sm:px-5 sm:py-2.5">
              <div className="flex shrink-0 -space-x-2.5">
                {["bg-rust", "bg-rust-deep", "bg-ink", "bg-rust-glow"].map((c, i) => (
                  <span key={i} className={`h-7 w-7 rounded-full ring-2 ring-background sm:h-8 sm:w-8 ${c}`} />
                ))}
              </div>
              <div className="max-w-[150px] text-left sm:max-w-none">
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-3 w-3 fill-rust text-rust" />
                  ))}
                </div>
                <span className="text-xs font-medium leading-snug text-foreground sm:text-sm">
                  Trusted by 5,000+ clients worldwide
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-28">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {stats.map((s) => (
            <div
              key={s.v}
              className="group flex flex-col items-center rounded-2xl border border-border bg-card p-6 text-center transition hover:-translate-y-1 hover:shadow-card md:p-8"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-ink text-cream transition group-hover:bg-gradient-accent">
                <s.icon className="h-5 w-5" />
              </div>
              <div className="mt-4 font-display text-4xl font-bold text-ink md:text-5xl">{s.k}</div>
              <div className="mt-2 text-sm text-muted-foreground">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-rust">What we do</p>
            <h2 className="mt-3 max-w-2xl text-4xl md:text-5xl font-bold">Disciplines built for outcomes.</h2>
          </div>
          <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-rust">
            See full services <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-14">
          {servicesLoading ? (
            <SectionSkeleton rows={6} className="md:grid-cols-2 lg:grid-cols-3" />
          ) : servicesError ? (
            <ErrorState message="Couldn't load services right now." />
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {activeServices.map((s, i) => {
                return (
                  <div
                    key={s.id}
                    className="group relative overflow-hidden rounded-2xl border border-border bg-card p-8 transition hover:-translate-y-1 hover:shadow-card"
                  >
                    <div className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-accent transition group-hover:scale-x-100" />
                    <div className="flex items-start justify-between">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-ink text-cream transition group-hover:bg-gradient-accent">
                        <AppIcon name={s.icon} className="h-5 w-5" />
                      </div>
                      {/* Decorative index number only — the card's real
                          content (icon, title, description) already
                          conveys everything a screen reader needs.
                          aria-hidden covers assistive tech, but Lighthouse's
                          color-contrast audit still evaluates hidden-from-AT
                          elements by their rendered pixels, so text-border
                          (1.39:1 against the card background) failed the
                          3:1 minimum for large bold text regardless. #8a7c6f
                          gets to ~3.4:1 — still clearly muted, just no
                          longer fails — verified empirically against the
                          live page (a hex arbitrary value, not oklch() —
                          `text-[oklch(0.78_0.02_50)]` silently failed to
                          generate any CSS at all, so that never actually
                          applied despite compiling without error). */}
                      <span
                        aria-hidden="true"
                        className="font-display text-3xl font-bold text-[#8a7c6f] transition group-hover:text-rust/20"
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="mt-6 text-xl font-semibold">{s.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.description}</p>
                    <Link
                      to="/services"
                      className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-rust transition md:opacity-0 md:group-hover:opacity-100"
                    >
                      {/* Lighthouse's SEO "descriptive link text" audit reads
                          the link's actual rendered text content, not
                          aria-label — an aria-label here (tried first)
                          still left it flagged. sr-only text folded into
                          the link itself keeps every card's link text
                          unique ("Learn more about X") for that audit and
                          for screen readers, while only the short "Learn
                          more" stays visible on screen. */}
                      Learn more <span className="sr-only">about {s.title}</span>{" "}
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                    <div className="absolute -right-8 -bottom-8 h-32 w-32 rounded-full bg-gradient-accent opacity-0 blur-3xl transition group-hover:opacity-30" />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {/* JOURNEY */}
      <section className="border-y border-border bg-secondary/30">
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-28">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-rust">How it works</p>
            <h2 className="mt-3 text-4xl md:text-5xl font-bold text-balance">
              Let your <span className="text-rust">software journey</span> begin.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
              From first conversation to launch day — here's how a project with Nexovia IT actually runs.
            </p>
          </div>

          <div className="mt-16 grid gap-10 md:grid-cols-4">
            {journey.map((step, i) => (
              <div key={step.title} className="relative flex flex-col items-center text-center">
                {i < journey.length - 1 && (
                  <div className="pointer-events-none absolute left-1/2 top-8 hidden h-px w-full border-t-2 border-dashed border-rust/30 md:block" />
                )}
                <div className="relative z-10 flex h-16 w-16 items-center justify-center rounded-2xl bg-ink text-cream shadow-card">
                  <step.icon className="h-6 w-6" />
                  <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-gradient-accent text-xs font-bold text-cream">
                    {i + 1}
                  </span>
                </div>
                <h3 className="mt-6 text-lg font-semibold">{step.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{step.body}</p>
                {i < journey.length - 1 && <ArrowRight className="mt-4 h-4 w-4 rotate-90 text-rust/50 md:hidden" />}
              </div>
            ))}
          </div>

          <div className="mt-14 flex justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant hover:opacity-95"
            >
              Start your project <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TECHNOLOGIES */}
      {(technologiesLoading || technologiesError || activeTechnologies.length > 0) && (
        <section className="mx-auto max-w-7xl px-6 py-24 md:py-28">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-rust">Technologies we use</p>
            <h2 className="mt-3 text-4xl md:text-5xl font-bold text-balance">
              Built on a <span className="text-rust">battle-tested stack</span>.
            </h2>
          </div>
          <div className="mt-14">
            {technologiesLoading ? (
              <SectionSkeleton rows={3} className="md:grid-cols-4" />
            ) : technologiesError ? (
              <ErrorState message="Couldn't load technologies right now." />
            ) : (
              <div className="overflow-hidden [mask-image:linear-gradient(90deg,transparent,black_10%,black_90%,transparent)]">
                <div className="flex w-max animate-marquee gap-6 hover:[animation-play-state:paused]">
                  {[...activeTechnologies, ...activeTechnologies].map((t, i) => (
                    <div
                      key={`${t.id}-${i}`}
                      className="flex h-24 w-48 shrink-0 items-center justify-center rounded-2xl border border-border bg-card px-6 shadow-card transition hover:-translate-y-1"
                    >
                      {t.logo_url ? (
                        <img
                          src={t.logo_url}
                          alt={t.name}
                          width={160}
                          height={32}
                          className="max-h-8 w-full object-contain"
                        />
                      ) : (
                        <span className="font-display text-lg font-bold text-foreground">{t.name}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* SHOWCASE BANNER */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-28">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-hero px-8 py-16 text-cream shadow-elegant md:px-16 md:py-24">
          <div className="absolute inset-0 grid-noise opacity-40" />
          <div className="relative grid gap-12 md:grid-cols-2 md:items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-cream/20 bg-cream/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-rust-glow backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-rust-glow" />
                Built for scale
              </span>
              <h2 className="mt-6 text-4xl font-bold leading-tight text-balance md:text-5xl">
                Start your software journey today.
              </h2>
              <p className="mt-4 max-w-md text-cream/70">
                From AI platforms to SaaS products, we turn ambitious ideas into software that ships — and keeps shipping.
              </p>
              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-cream px-6 py-3 text-sm font-semibold text-ink hover:bg-cream/90"
              >
                Explore Now <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="relative hidden h-72 md:block">
              {[
                { icon: Cpu, label: "AI & ML", className: "left-0 top-0 rotate-[-8deg]" },
                { icon: Layers, label: "SaaS Platforms", className: "left-28 top-10 rotate-[4deg]" },
                { icon: Database, label: "Data Engineering", className: "left-4 top-36 rotate-[6deg]" },
                { icon: CloudCog, label: "Cloud & DevOps", className: "left-40 top-40 rotate-[-5deg]" },
              ].map(({ icon: Icon, label, className }) => (
                <div
                  key={label}
                  className={`absolute flex w-44 flex-col gap-3 rounded-2xl border border-cream/15 bg-cream/[0.07] p-5 shadow-elegant backdrop-blur transition hover:-translate-y-1 hover:bg-cream/[0.12] ${className}`}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-accent text-cream">
                    <Icon className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-semibold">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {(testimonialsLoading || testimonialsError || activeTestimonials.length > 0) && (
        <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-28">
          <p className="text-xs font-semibold uppercase tracking-widest text-rust">Testimonials</p>
          <h2 className="mt-3 max-w-2xl text-4xl md:text-5xl font-bold">Trusted by teams that ship.</h2>
          <div className="mt-12">
            {testimonialsLoading ? (
              <SectionSkeleton rows={3} />
            ) : testimonialsError ? (
              <ErrorState message="Couldn't load testimonials right now." />
            ) : (
              <div className="grid gap-6 md:grid-cols-3">
                {activeTestimonials.map((t) => (
                  <div
                    key={t.id}
                    className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card p-8 transition hover:-translate-y-1 hover:shadow-card"
                  >
                    <div className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-accent transition group-hover:scale-x-100" />
                    <Quote className="absolute right-6 top-6 h-10 w-10 text-border transition group-hover:text-rust/15" />
                    <div className="relative flex gap-1 text-rust">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className={`h-4 w-4 ${i < t.rating ? "fill-rust" : "opacity-25"}`} />
                      ))}
                    </div>
                    <p className="relative mt-4 flex-1 text-sm text-muted-foreground leading-relaxed">"{t.quote}"</p>
                    <div className="relative mt-6 flex items-center gap-3 border-t border-border pt-5">
                      {t.avatar_url ? (
                        <img
                      src={t.avatar_url}
                      alt={t.client_name}
                      width={44}
                      height={44}
                      className="h-11 w-11 rounded-full object-cover object-top"
                    />
                      ) : (
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ink text-sm font-semibold text-cream">
                          {t.client_name
                            .split(" ")
                            .map((w) => w[0])
                            .slice(0, 2)
                            .join("")
                            .toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-semibold">{t.client_name}</p>
                        {t.client_role && <p className="text-xs text-muted-foreground">{t.client_role}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* SPLIT CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-28">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-accent p-10 text-cream shadow-elegant md:p-16">
          <div className="absolute inset-0 grid-noise opacity-25" />
          <div className="absolute -right-16 -top-24 h-72 w-72 rounded-full bg-cream/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-10 h-64 w-64 rounded-full bg-ink/20 blur-3xl" />

          <div className="relative flex flex-col gap-10 md:flex-row md:items-center md:justify-between">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-cream/30 bg-cream/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-cream" />
                Why teams choose us
              </span>
              <h2 className="mt-5 text-4xl font-bold leading-tight text-balance md:text-5xl">
                A partner, not a vendor.
              </h2>
              <p className="mt-4 text-cream/80">
                We embed with your team, ship in weekly cycles and stay accountable to outcomes — MRR, retention, time-to-market.
              </p>
              <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-cream/90">
                {["Weekly ship cycles", "Senior engineers only", "Outcomes, not hours"].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-cream/20">
                      <span className="h-1.5 w-1.5 rounded-full bg-cream" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="shrink-0">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-cream px-7 py-3.5 text-sm font-semibold text-ink shadow-elegant transition hover:-translate-y-0.5 hover:bg-cream/90"
              >
                Start your project <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
